/**
 * WorkflowTaskListFooter.tsx — 合并多 loop 任务列表底部 pill（§5.11.4）
 */
import { useMemo, useState } from 'react';
import { useStore } from 'zustand';

import { useWorkflowSessionStoreApi } from '../../core/context/WorkflowSessionContext';
import type { WorkflowSessionStoreState } from '../../core/workflowSessionStore';
import { TaskList } from '../TaskList';
import {
  deriveWorkflowTaskFooterState,
  formatWorkflowTaskFooterLabel,
  groupWorkflowTasksByLoop,
  loopSessionDisplayLabel,
  shouldShowWorkflowTaskListFooter,
} from './workflowTaskListMerge';

function MessageResponse({ children }: { children: React.ReactNode }) {
  return (
    <div className="lax-message-response">
      <span className="lax-message-response__marker">⎿ </span>
      <span className="lax-message-response__content">{children}</span>
    </div>
  );
}

export function WorkflowTaskListFooter() {
  const store = useWorkflowSessionStoreApi();
  const state = useStore(store, (s: WorkflowSessionStoreState) => s.state);
  const [expanded, setExpanded] = useState(false);

  const footerState = useMemo(() => deriveWorkflowTaskFooterState(state), [state]);
  const visible = shouldShowWorkflowTaskListFooter(state);

  if (!visible) {
    return null;
  }

  const label = formatWorkflowTaskFooterLabel(state);
  const groups = groupWorkflowTasksByLoop(footerState.tasks);

  return (
    <div className="lax-workflow-task-list-footer" data-testid="lax-workflow-task-list-footer">
      <button
        type="button"
        className="lax-task-list-footer__pill lax-workflow-task-list-footer__pill"
        data-testid="lax-workflow-task-list-footer-pill"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {label}
        <span className="lax-task-list-footer__hint">
          {expanded ? ' · ↑ to hide' : ' · ↓ to view'}
        </span>
      </button>
      {expanded ? (
        <MessageResponse>
          <div className="lax-workflow-task-list-groups">
            {groups.map((group) => (
              <div
                key={group.loopSessionId}
                className="lax-workflow-task-list-group"
                data-testid={`lax-workflow-task-group-${group.loopSessionId}`}
              >
                <div className="lax-workflow-task-list-group-title">
                  {loopSessionDisplayLabel(group.loopSessionId)}
                </div>
                <TaskList tasks={group.tasks} />
              </div>
            ))}
          </div>
        </MessageResponse>
      ) : null}
    </div>
  );
}
